import React, { useState, useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Users, Settings, LogOut,
  Calendar, Image, Building2, Film, Bell,
  Search, Globe, ChevronDown, CheckCircle,
  AlertCircle, Menu, X as CloseIcon, TrendingUp
} from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import './dashboard.css';
import { API_URL } from '../../../config.js'
import { userAPI, eventAPI, galleryAPI, logoAPI, mediaAPI } from '../../../services/dashboardAPI';
import UserManagement from './sections/UserManagement';
import EventManagement from './sections/EventManagement';
import GalleryManagement from './sections/GalleryManagement';
import LogoManagement from './sections/LogoManagement';
import MediaManagement from './sections/MediaManagement';
import SettingsSection from './sections/SettingsSection';

const chartData = [
  { name: 'Jan', value: 400 },
  { name: 'Feb', value: 300 },
  { name: 'Mar', value: 600 },
  { name: 'Apr', value: 800 },
  { name: 'May', value: 500 },
  { name: 'Jun', value: 900 },
];

const ROLE_PERMISSIONS = {
  super_admin:     ['users', 'events', 'gallery', 'logos', 'media', 'settings'],
  event_manager:   ['events'],
  gallery_manager: ['gallery'],
  logo_manager:    ['logos'],
  media_manager:   ['media'],
};


const API = API_URL;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('dashboard');
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth > 768);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [currentUser] = useState({ role: 'super_admin', name: 'Admin User', email: 'admin@ihn.com' });
  const [notification, setNotification] = useState(null);
  const [loading, setLoading] = useState(true);
  const [users,   setUsers]   = useState([]);
  const [events,  setEvents]  = useState([]);
  const [gallery, setGallery] = useState([]);
  const [logos,   setLogos]   = useState([]);
  const [media,   setMedia]   = useState([]);

  // ── Responsive sidebar ──
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      setIsSidebarOpen(!mobile);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const showNotification = useCallback((message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3500);
  }, []);

  // ── Auth check — JWT only ──
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('ihn_admin_token');
      if (!token) { navigate('/admin', { replace: true }); return; }

      try {
        const res = await fetch(`${API}/admin/verify`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        if (!data.success) {
          localStorage.removeItem('ihn_admin_token');
          navigate('/admin', { replace: true });
        }
      } catch {
        localStorage.removeItem('ihn_admin_token');
        navigate('/admin', { replace: true });
      }
    };
    checkAuth();
  }, [navigate]);

  // ── Fetch data ──
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [usersRes, eventsRes, galleryRes, logosRes, mediaRes] = await Promise.all([
          userAPI.getAll(),
          eventAPI.getAll(),
          galleryAPI.getAll(),
          logoAPI.getAll(),
          mediaAPI.getAll(),
        ]);
        if (usersRes.success)   setUsers(usersRes.data   || []);
        if (eventsRes.success)  setEvents(eventsRes.data  || []);
        if (galleryRes.success) setGallery(galleryRes.data || []);
        if (logosRes.success)   setLogos(logosRes.data   || []);
        if (mediaRes.success)   setMedia(mediaRes.data   || []);
      } catch {
        showNotification('Failed to load data', 'error');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [showNotification]);

  // ── Logout ──
  const handleLogout = () => {
    localStorage.removeItem('ihn_admin_token');
    navigate('/admin');
  };

  // ── Mobile nav ──
  const handleNavSelect = (section) => {
    setActiveSection(section);
    if (isMobile) setIsSidebarOpen(false);
  };

  const canAccess = (section) =>
    ROLE_PERMISSIONS[currentUser.role]?.includes(section) || section === 'dashboard';

  const stats = [
    { icon: <Users size={18} />,     label: 'Total Users',   value: users.length,   type: 'success' },
    { icon: <Calendar size={18} />,  label: 'Total Events',  value: events.length,  type: 'primary' },
    { icon: <Image size={18} />,     label: 'Gallery Items', value: gallery.length, type: 'warning' },
    { icon: <Building2 size={18} />, label: 'Company Logos', value: logos.length,   type: 'info'    },
  ];

  return (
    <div className={`admin-container ${isSidebarOpen && isMobile ? 'sidebar-open' : ''}`}>

      {/* SIDEBAR */}
      <aside className="admin-sidebar">
        <div className="sidebar-brand">
          <div className="brand-logo"><Globe size={18} /></div>
          <span className="brand-name">IHN Panel</span>
        </div>

        <nav className="sidebar-nav">
          <span className="sidebar-section-label">Main</span>

          <NavItem
            icon={<LayoutDashboard size={16} />}
            label="Dashboard"
            active={activeSection === 'dashboard'}
            onClick={() => handleNavSelect('dashboard')}
          />

          {canAccess('users') && (
            <NavItem
              icon={<Users size={16} />}
              label="Users & Roles"
              active={activeSection === 'users'}
              onClick={() => handleNavSelect('users')}
            />
          )}

     {canAccess('events') && (
  <>
    <span className="sidebar-section-label" style={{ marginTop: 8 }}>Content</span>
    <NavItem
      icon={<Calendar size={16} />}
      label="Events"
      active={activeSection === 'events'}
      onClick={() => handleNavSelect('events')}
    />
  </>
)}

          {canAccess('gallery') && (
            <NavItem
              icon={<Image size={16} />}
              label="Gallery"
              active={activeSection === 'gallery'}
              onClick={() => handleNavSelect('gallery')}
            />
          )}

          {canAccess('logos') && (
            <NavItem
              icon={<Building2 size={16} />}
              label="Logos"
              active={activeSection === 'logos'}
              onClick={() => handleNavSelect('logos')}
            />
          )}

          {canAccess('media') && (
            <NavItem
              icon={<Film size={16} />}
              label="Media"
              active={activeSection === 'media'}
              onClick={() => handleNavSelect('media')}
            />
          )}

          {canAccess('settings') && (
            <>
              <span className="sidebar-section-label" style={{ marginTop: 8 }}>System</span>
              <NavItem
                icon={<Settings size={16} />}
                label="Settings"
                active={activeSection === 'settings'}
                onClick={() => handleNavSelect('settings')}
              />
            </>
          )}
        </nav>

        <div className="sidebar-footer">
          <div className="admin-profile">
            <div className="avatar">
              {currentUser.name.charAt(0)}{currentUser.name.split(' ')[1]?.charAt(0) || ''}
            </div>
            <div className="admin-profile-text">
              <div className="admin-name">{currentUser.name}</div>
              <div className="admin-role">{currentUser.role.replace(/_/g, ' ')}</div>
            </div>
          </div>
          <button className="logout-btn" onClick={handleLogout}>
            <LogOut size={15} /> Sign Out
          </button>
        </div>
      </aside>

      {/* MAIN */}
      <main className="admin-main">
        <header className="content-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button
              className="hamburger-menu"
              onClick={() => setIsSidebarOpen((o) => !o)}
              aria-label="Toggle sidebar"
            >
              {isSidebarOpen && isMobile ? <CloseIcon size={16} /> : <Menu size={16} />}
            </button>
            <div className="search-bar">
              <Search size={15} />
              <input placeholder="Search anything…" />
            </div>
          </div>

          <div className="header-actions">
            {/* Section breadcrumb on mobile */}
            {isMobile && (
              <span style={{
                fontSize: '0.82rem', fontWeight: 600,
                color: 'var(--text-secondary)',
                textTransform: 'capitalize',
              }}>
                {activeSection.replace(/_/g, ' ')}
              </span>
            )}
            <button className="icon-notification">
              <Bell size={16} />
            </button>
          </div>
        </header>

        <div className="content-body">
          {activeSection === 'dashboard' && (
            <DashboardOverview stats={stats} data={chartData} loading={loading} />
          )}
          {activeSection === 'users' && (
            <UserManagement
              users={users}
              setUsers={setUsers}
              showNotification={showNotification}
              currentRole={currentUser.role}
            />
          )}
        {activeSection === 'events' && (
  <EventManagement
    events={events}
    setEvents={setEvents}
    showNotification={showNotification}
  />
)}
          {activeSection === 'gallery' && (
            <GalleryManagement
              gallery={gallery}
              setGallery={setGallery}
              showNotification={showNotification}
            />
          )}
          {activeSection === 'logos' && (
            <LogoManagement
              logos={logos}
              setLogos={setLogos}
              showNotification={showNotification}
            />
          )}
          {activeSection === 'media' && (
            <MediaManagement
              media={media}
              setMedia={setMedia}
              showNotification={showNotification}
            />
          )}
          {activeSection === 'settings' && (
            <SettingsSection showNotification={showNotification} />
          )}
        </div>
      </main>

      {/* NOTIFICATION */}
      {notification && (
        <div className={`notification ${notification.type}`}>
          {notification.type === 'success'
            ? <CheckCircle size={16} />
            : <AlertCircle size={16} />}
          {notification.message}
        </div>
      )}
    </div>
  );
}

/* ════════════════════ SUB-COMPONENTS ════════════════════ */

const NavItem = ({ icon, label, active, onClick }) => (
  <button className={`nav-link ${active ? 'active' : ''}`} onClick={onClick}>
    <span className="link-content">{icon}{label}</span>
  </button>
);

const NavDropdown = ({ icon, label, items, onSelect, activeSection }) => {
  const [open, setOpen] = useState(false);
  const isActive = items.some((i) => i.section === activeSection);

  return (
    <div className={`dropdown-group ${open || isActive ? 'is-open' : ''}`}>
      <button
        className={`nav-link ${isActive ? 'active' : ''}`}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="link-content">{icon}{label}</span>
        <ChevronDown
          size={13}
          style={{
            transition: 'transform 0.2s',
            transform: open || isActive ? 'rotate(180deg)' : 'rotate(0deg)',
            flexShrink: 0,
          }}
        />
      </button>
      <div className="submenu-container">
        {items.map((i) => (
          <button
            key={i.section}
            className={`submenu-item ${activeSection === i.section ? 'active' : ''}`}
            onClick={() => { onSelect(i.section); setOpen(false); }}
          >
            {i.label}
          </button>
        ))}
      </div>
    </div>
  );
};

/* ════════════════════ DASHBOARD OVERVIEW ════════════════════ */
const DashboardOverview = ({ stats, data, loading }) => (
  <>
    <div style={{ marginBottom: 24 }}>
      <h2 className="title">Dashboard</h2>
      <p className="subtitle">Welcome back — here's what's happening at IHN</p>
    </div>

    <div className="stats-grid">
      {stats.map((s, i) => (
        <div key={i} className={`stat-card ${s.type}`}>
          <div className="stat-icon-wrapper">{s.icon}</div>
          <div>
            <span className="stat-label">{s.label}</span>
            <div className="stat-value">
              {loading ? '—' : s.value.toLocaleString()}
            </div>
          </div>
        </div>
      ))}
    </div>

    <div className="main-chart-card">
      <div className="card-header">
        <h3>
          <TrendingUp size={16} style={{ color: 'var(--brand)' }} />
          Network Activity
        </h3>
        <span className="badge">Last 6 months</span>
      </div>
      <div className="chart-wrapper">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 4, right: 0, left: -20, bottom: 0 }}>
            <defs>
              <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"   stopColor="#861b68" stopOpacity={0.18} />
                <stop offset="100%" stopColor="#861b68" stopOpacity={0}    />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(0,0,0,0.05)" />
            <XAxis
              dataKey="name"
              stroke="transparent"
              tick={{ fill: '#a398ae', fontSize: 12, fontFamily: 'Outfit' }}
            />
            <YAxis
              stroke="transparent"
              tick={{ fill: '#a398ae', fontSize: 12, fontFamily: 'Outfit' }}
            />
            <Tooltip
              contentStyle={{
                background: '#fff',
                border: '1px solid rgba(0,0,0,0.08)',
                borderRadius: 10,
                boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
                fontSize: 13,
                fontFamily: 'Outfit',
              }}
              cursor={{ stroke: 'rgba(134,27,104,0.15)', strokeWidth: 2 }}
            />
            <Area
              type="monotone"
              dataKey="value"
              stroke="#861b68"
              strokeWidth={2}
              fill="url(#areaGrad)"
              dot={false}
              activeDot={{ r: 5, fill: '#861b68', strokeWidth: 2, stroke: '#fff' }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  </>
);
